import axios from "axios";
import { API_ROOT } from ".";

// Contentful-backed public content endpoints.
const CONTENT_API_URL = `${API_ROOT}/content`;

export interface CompanyDetails {
  companyName: string;
  companyEmailAddress: string;
  businessOrderEmail: string;
  facebookLink: string;
  instagramLink: string;
  linkedinLink: string;
  primaryPhoneNumber?: string;
  supportUsLink?: string;
  lastSynced: string;
}

export interface Event {
  _id: string;
  poster: string;
  eventName: string;
  eventStartTime: string;
  eventEndTime: string;
  availableTicketCount?: number;
  purchasedTicketCount?: number;
  reservedTicketCount?: number;
  fullDescription?: string;
  eventTypes: string[];
  ticketPrice: number;
  eventLocation: string;
  lastSynced: string;
}

/** Wire shape from `/api/content/events` — type-first keys match Mongo/Contentful. */
type EventApiResponse = {
  _id: string;
  poster: string;
  eventName: string;
  eventStartTime: string;
  eventEndTime: string;
  numberOfAvailableTickets?: number;
  numberOfPurchasedTickets?: number;
  numberOfReservedTickets?: number;
  fullDescription?: string;
  typeOfEvent?: string[];
  priceOfTicket: number;
  locationOfEvent: string;
  lastSynced: string;
};

function mapEventFromApi(raw: EventApiResponse): Event {
  return {
    _id: raw._id,
    poster: raw.poster,
    eventName: raw.eventName,
    eventStartTime: raw.eventStartTime,
    eventEndTime: raw.eventEndTime,
    availableTicketCount: raw.numberOfAvailableTickets,
    purchasedTicketCount: raw.numberOfPurchasedTickets,
    reservedTicketCount: raw.numberOfReservedTickets,
    fullDescription: raw.fullDescription,
    eventTypes: raw.typeOfEvent ?? [],
    ticketPrice: raw.priceOfTicket,
    eventLocation: raw.locationOfEvent,
    lastSynced: raw.lastSynced,
  };
}

export interface StaffRole {
  contentfulId: string;
  title: string;
  fullDescription?: string;
}

export interface TeamMember {
  contentfulId: string;
  name: string;
  surname: string;
  academicInstitution?: string;
  studyField?: string;
  staffTitle?: string;
  pronouns?: string;
  role: StaffRole[];
  startDate: string;
  endDate?: string;
  profilePicture: string;
}

type TeamMemberApiResponse = Omit<TeamMember, "studyField"> & {
  fieldOfStudy?: string;
};

function mapTeamMemberFromApi(raw: TeamMemberApiResponse): TeamMember {
  const { fieldOfStudy, ...rest } = raw;
  return {
    ...rest,
    studyField: fieldOfStudy,
  };
}

export interface SponsorTier {
  contentfulId: string;
  title: string;
  order: number;
  description?: string;
}

export interface Sponsor {
  contentfulId: string;
  name: string;
  logoUrl: string;
  websiteUrl?: string;
  tier: SponsorTier;
}

export interface MembershipOffering {
  contentfulId: string;
  name: string;
  slug: string;
  coverImage: string;
  description?: string;
  price: number;
}

export interface MembershipsCatalog {
  memberships: MembershipOffering[];
}

export type GalleryMedia = {
  url: string;
  lightboxUrl?: string;
  type: "image" | "video";
};

export interface GallerySection {
  contentfulId: string;
  title: string;
  order: number;
  media: GalleryMedia[];
}

export interface VolunteerSection {
  contentfulId: string;
  sectionText: string;
}

export interface AgroecologyResourceLink {
  contentfulId: string;
  title: string;
  link: string;
}

export interface MarketInformation {
  contentfulId: string;
  locationName: string;
  /** Full address for Maps links (reverse-geocoded from the pin). */
  address?: string;
  location: { lat: number; lon: number };
  weekday: string;
  startTime: string;
  endTime: string;
}

export interface MarketCardOnHomePage {
  contentfulId: string | null;
  title: string | null;
  marketInformation?: MarketInformation[];
}
// Fetch company details
let companyDetailsRequest: Promise<CompanyDetails> | null = null;

export async function fetchCompanyDetails(options?: {
  fresh?: boolean;
}): Promise<CompanyDetails> {
  if (options?.fresh) companyDetailsRequest = null;
  if (!companyDetailsRequest) {
    companyDetailsRequest = axios
      .get<CompanyDetails>(`${CONTENT_API_URL}/companyDetails`)
      .then((res) => res.data)
      .finally(() => {
        companyDetailsRequest = null;
      });
  }
  return companyDetailsRequest;
}

// Fetch events
let eventsRequest: Promise<Event[]> | null = null;

export async function fetchEvents(options?: {
  fresh?: boolean;
}): Promise<Event[]> {
  if (options?.fresh) eventsRequest = null;
  if (!eventsRequest) {
    eventsRequest = axios
      .get<EventApiResponse[]>(`${CONTENT_API_URL}/events`)
      .then((res) => res.data.map(mapEventFromApi))
      .finally(() => {
        eventsRequest = null;
      });
  }
  return eventsRequest;
}

// Fetch staff roles
export async function fetchStaffRoles(): Promise<StaffRole[]> {
  const res = await axios.get<StaffRole[]>(`${CONTENT_API_URL}/staffRoles`);
  return res.data;
}

// Fetch team members
export async function fetchTeamMembers(): Promise<TeamMember[]> {
  const res = await axios.get<TeamMemberApiResponse[]>(
    `${CONTENT_API_URL}/teamMembers`,
  );
  return res.data.map(mapTeamMemberFromApi);
}

//Fetch sponsor tiers
export async function fetchSponsorTiers(): Promise<SponsorTier[]> {
  const res = await axios.get<SponsorTier[]>(`${CONTENT_API_URL}/sponsorTiers`);
  return res.data;
}

//Fetch sponsors
export async function fetchSponsors(): Promise<Sponsor[]> {
  const res = await axios.get<Sponsor[]>(`${CONTENT_API_URL}/sponsors`);
  return res.data;
}

export async function fetchGallerySections(): Promise<GallerySection[]> {
  const res = await axios.get<GallerySection[]>(
    `${CONTENT_API_URL}/gallerySections`,
  );
  return res.data;
}

export async function fetchVolunteerSection(): Promise<VolunteerSection> {
  const res = await axios.get<VolunteerSection>(
    `${CONTENT_API_URL}/volunteerSection`,
  );
  return res.data;
}

export async function fetchAgroecologyResourceLinks(): Promise<
  AgroecologyResourceLink[]
> {
  const res = await axios.get<AgroecologyResourceLink[]>(
    `${CONTENT_API_URL}/agroecologyResourceLinks`,
  );
  return res.data;
}

export async function fetchMembershipsCatalog(): Promise<MembershipsCatalog> {
  const res = await axios.get<MembershipsCatalog>(
    `${CONTENT_API_URL}/membershipsCatalog`,
    { timeout: 15000 },
  );
  return res.data;
}

export async function fetchMarketInformation(): Promise<MarketInformation[]> {
  const res = await axios.get<MarketInformation[]>(
    `${CONTENT_API_URL}/marketInformation`,
  );
  return res.data;
}

export async function fetchMarketCardOnHomePage(): Promise<MarketCardOnHomePage> {
  const res = await axios.get<MarketCardOnHomePage>(
    `${CONTENT_API_URL}/marketCardOnHomePage`,
  );
  return res.data;
}

export interface WishlistItem {
  contentfulId: string;
  title: string;
  description: string;
  imageUrl: string;
}

export async function fetchWishlistItems(): Promise<WishlistItem[]> {
  const res = await axios.get<WishlistItem[]>(
    `${CONTENT_API_URL}/wishlistItems`,
  );
  return res.data;
}

export interface ContentfulDocumentItem {
  fields?: {
    title?: string;
    file?: {
      fields?: {
        file?: {
          url?: string;
        };
      };
    };
  };
}

export interface ContentfulDocumentsResponse {
  items: ContentfulDocumentItem[];
}

export async function fetchContentDocuments(): Promise<ContentfulDocumentsResponse> {
  const res = await axios.get<ContentfulDocumentsResponse>(
    `${CONTENT_API_URL}/documents`,
  );
  return res.data;
}

export interface SubscriptionBoxContentfulItem {
  fields: {
    title: string;
    description: string[];
    image: { fields: { file: { url: string } } };
  };
}

export interface SubscriptionBoxesResponse {
  items: SubscriptionBoxContentfulItem[];
}

export async function fetchSubscriptionBoxes(): Promise<SubscriptionBoxesResponse> {
  const res = await axios.get<SubscriptionBoxesResponse>(
    `${CONTENT_API_URL}/subscriptionBoxes`,
  );
  return res.data;
}
